import { css } from '@emotion/react';
import { breakpointInfix, MediaBreakpointUp } from './mixins/_breakpoints.mixin';
import { Caret } from './mixins/_caret.mixin';
import { FontSize } from './vendor/_rfs.mixin';
import { BorderRadius, BorderTopRadius, BorderBottomRadius } from './mixins/_border-radius.mixin';
import { BoxShadow } from './mixins/_box-shadow.mixin';
import { HoverFocus } from './mixins/_hover.mixin';
import { GradientBg } from './mixins/_gradients.mixin';
import { NavDivider } from './mixins/_nav-divider.mixin';

export default $ => {
  const rules = [];

  rules.push(css`
    .dropup,
    .dropright,
    .dropdown,
    .dropleft {
      position: relative;
    }
  `);

  rules.push(css`
    .dropdown-toggle {
      white-space: nowrap;

      // Generate the caret automatically
      ${new Caret().include($)};
    }
  `);

  // The dropdown menu
  rules.push(css`
    .dropdown-menu {
      position: absolute;
      top: 100%;
      left: 0;
      z-index: ${$.zindexDropdown};
      display: none;
      float: left;
      min-width: ${$.dropdownMinWidth};
      padding: ${$.dropdownPaddingY} ${$.dropdownPaddingX};
      margin: ${$.dropdownSpacer} 0 0;
      ${new FontSize($.dropdownFontSize).include($)};
      color: ${$.dropdownColor};
      text-align: left;
      list-style: none;
      background-color: ${$.dropdownBg};
      background-clip: padding-box;
      border: ${$.dropdownBorderWidth} solid ${$.dropdownBorderColor};
      ${new BorderRadius($.dropdownBorderRadius).include($)};
      ${new BoxShadow($.dropdownBoxShadow).include($)};
    }
  `);

  Object.keys($.gridBreakpoints).forEach(breakpoint => {
    const infix = breakpointInfix($, breakpoint, $.gridBreakpoints);
    rules.push(
      new MediaBreakpointUp(breakpoint).include(
        $,
        css`
          .dropdown-menu${infix}-left {
            right: auto;
            left: 0;
          }

          .dropdown-menu${infix}-right {
            right: 0;
            left: auto;
          }
        `
      )
    );
  });

  rules.push(css`
    .dropup {
      .dropdown-menu {
        top: auto;
        bottom: 100%;
        margin-top: 0;
        margin-bottom: ${$.dropdownSpacer};
      }

      .dropdown-toggle {
        ${new Caret('up').include($)};
      }
    }
  `);

  rules.push(css`
    .dropright {
      .dropdown-menu {
        top: 0;
        right: auto;
        left: 100%;
        margin-top: 0;
        margin-left: ${$.dropdownSpacer};
      }

      .dropdown-toggle {
        ${new Caret('right').include($)};
        &::after {
          vertical-align: 0;
        }
      }
    }
  `);

  rules.push(css`
    .dropleft {
      .dropdown-menu {
        top: 0;
        right: 100%;
        left: auto;
        margin-top: 0;
        margin-right: ${$.dropdownSpacer};
      }

      .dropdown-toggle {
        ${new Caret('left').include($)};
        &::before {
          vertical-align: 0;
        }
      }
    }
  `);

  rules.push(css`
    .dropdown-menu {
      &[x-placement^='top'],
      &[x-placement^='right'],
      &[x-placement^='bottom'],
      &[x-placement^='left'] {
        right: auto;
        bottom: auto;
      }
    }
  `);

  rules.push(css`
    .dropdown-divider {
      ${new NavDivider($.dropdownDividerBg, $.dropdownDividerMarginY, true).include($)};
    }
  `);

  rules.push(css`
    .dropdown-item {
      display: block;
      width: 100%; // For \`<button>\`s
      padding: ${$.dropdownItemPaddingY} ${$.dropdownItemPaddingX};
      clear: both;
      font-weight: ${$.fontWeightNormal};
      color: ${$.dropdownLinkColor};
      text-align: inherit; // For \`<button>\`s
      text-decoration: ${$.linkDecoration === 'none' ? 'null' : 'none'};
      white-space: nowrap; // prevent links from randomly breaking onto new lines
      background-color: transparent; // For \`<button>\`s
      border: 0; // For \`<button>\`s

      // Prevent dropdown overflow if there's no padding
      // See https://github.com/twbs/bootstrap/pull/27703
      ${$.dropdownPaddingY === 0
        ? css`
            &:first-of-type {
              ${new BorderTopRadius($.dropdownInnerBorderRadius).include($)};
            }

            &:last-child {
              ${new BorderBottomRadius($.dropdownInnerBorderRadius).include($)};
            }
          `
        : css``}

      ${new HoverFocus().include(
        $,
        css`
          color: ${$.dropdownLinkHoverColor};
          text-decoration: none;
          ${new GradientBg($.dropdownLinkHoverBg).include($)};
        `
      )}

      &.active,
      &:active {
        color: ${$.dropdownLinkActiveColor};
        text-decoration: none;
        ${new GradientBg($.dropdownLinkActiveBg).include($)};
      }

      &.disabled,
      &:disabled {
        color: ${$.dropdownLinkDisabledColor};
        pointer-events: none;
        background-color: transparent;
        // Remove CSS gradients if they're enabled
        ${$.enableGradients
          ? css`
              background-image: none;
            `
          : css``}
      }
    }
  `);

  rules.push(css`
    .dropdown-menu.show {
      display: block;
    }
  `);

  rules.push(css`
    .dropdown-header {
      display: block;
      padding: ${$.dropdownHeaderPadding};
      margin-bottom: 0;
      ${new FontSize($.fontSizeSm).include($)};
      color: ${$.dropdownHeaderColor};
      white-space: nowrap;
    }
  `);

  rules.push(css`
    .dropdown-item-text {
      display: block;
      padding: ${$.dropdownItemPaddingY} ${$.dropdownItemPaddingX};
      color: ${$.dropdownLinkColor};
    }
  `);

  return css(rules);
};
